import {
  Card,
  Typography,
  Input,
  Button,
  Switch,
  Table
} from "antd";
import { DeleteOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../../Modal/ConfirmationModal";
import { useState, useEffect } from "react";
import * as Apis from "../../utils/helper/enum";
import { changeLoader } from "../../redux/reducers/loader";
import { useDispatch } from "react-redux";
import globalRequest from "../../utils/global/globalRequest";
import toast from "react-hot-toast";
import { paginationLimit } from "../../utils/helper/constants";
import { checkPermissionAction, formatDateFunction } from "../../utils/helper/utils";
import {
  addDeleteGetLocalStorage,
  storageKeys,
} from "../../utils/global/localData";
import { jwtDecode } from "jwt-decode";
let timer = null;

function ManageSubscriptions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [paginationModel, setPaginationModel] = useState(paginationLimit);
  const [totalRows, setTotalRows] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemData, setItemData] = useState(0);
  const [searchValue, setSearchValue] = useState("");


  const userAccess = addDeleteGetLocalStorage(storageKeys?.USER_TOKEN, {}, "get");
  let permissionJson = [];
  if (userAccess) {
    const decoded = jwtDecode(userAccess);
    permissionJson = decoded?.user?.rolePermission?.permissionJson;
  }

  const getAllSubscriptions = async (search = "", page = paginationModel.page) => {
    dispatch(changeLoader(true));
    try {
      let response = await globalRequest(
        `${Apis.MANAGE_SUBSCRIPTION.GET_ALL_SUBSCRIPTIONS}?search=${search}&page=${page}&limit=${paginationModel?.limit}`,
        "get",
        {},
        {},
        true
      );
      let apiResponse = response?.data;
      if (response?.status === 200) {
        setList(apiResponse?.data?.data || []);
        setTotalRows(apiResponse?.data?.paginator?.itemCount || 0);
      } else {
        toast.error(apiResponse?.message);
      }
    } catch (e) {
      toast.error(e?.message);
    }
    dispatch(changeLoader(false));
  };

  const handleStatusChange = async (item) => {
    try {
      let value = { status: item.subscriptionStatus === "active" ? "inactive" : "active" };
      let response = await globalRequest(
        `${Apis?.MANAGE_SUBSCRIPTION?.UPDATE_SUBSCRIPTION_STATUS}/${Number(item.id)}`,
        "put",
        value,
        {},
        true
      );
      let apiResponse = response?.data;
      if (response?.status === 200) {
        let msg = "Subscription active successfully";
        if (item.subscriptionStatus === "active") msg = "Subscription inactive successfully";
        toast.success(msg, {
          duration: 2000,
        });
        getAllSubscriptions();
      } else {
        toast.error(apiResponse?.message);
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };
  const handleDeleteOk = async () => {
    if (itemData.id === 1) {
      toast.error("You are not able to delete this role");
      return false;
    }
    setIsDeleteModalOpen(false);
    try {
      let value = { status: "deleted" };
      let response = await globalRequest(
        `${Apis?.MANAGE_SUBSCRIPTION?.UPDATE_SUBSCRIPTION_STATUS}/${Number(itemData.id)}`,
        "put",
        value,
        {},
        true
      );
      let apiResponse = response?.data;
      if (response?.status === 200) {
        let msg = "Subscription deleted successfully";
        toast.success(msg, {
          duration: 2000,
        });
        getAllSubscriptions();
      } else {
        toast.error(apiResponse?.message);
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  useEffect(() => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      getAllSubscriptions();
    }, 400);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      getAllSubscriptions(searchValue, paginationModel.page);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchValue, paginationModel.page]);

  const columns = [
    {
      title: "Subscription ID",
      dataIndex: "subscriptionId",
      key: "subscriptionId",
      sorter: (a, b) => a.subscriptionId - b.subscriptionId,
      render: (text) => <a>{text}</a>,
    },
    {
      title: "Plan Name",
      dataIndex: "planName",
      key: "planName",
      sorter: (a, b) => {
        const nameA = a?.locales?.find(item => item.languageCode === "en")?.planName || "";
        const nameB = b?.locales?.find(item => item.languageCode === "en")?.planName || "";
        return nameA.localeCompare(nameB);
      },
      render: (_, record) => {
        const plan = record?.locales?.find(item => item.languageCode === "en");
        return <span>{plan?.planName || "—"}</span>;
      },
    },
    {
      title: "Plan Price",
      dataIndex: "planPrice",
      key: "planPrice",
      sorter: (a, b) => a.planPrice - b.planPrice,
      render: (text, record) => (
        <span>
          SAR {record.planPrice} / {record.planDuration === "monthly" ? "Month" : "Year"}
        </span>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      sorter: (a, b) => a.startDate.localeCompare(b.startDate),
      render: (text) => <span>{formatDateFunction(text)}</span>,
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      sorter: (a, b) => a.endDate.localeCompare(b.endDate),
      render: (text) => <span>{formatDateFunction(text)}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status - b.status,
      render: (text, record) => (
        <div className="flex items-center gap-2">
          <Switch
            checked={record?.subscriptionStatus === "active" ? true : false}
            onChange={() => handleStatusChange(record)}
          />
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text, record) => (
        <div className="flex gap-2">
          <EditOutlined onClick={() => navigate(`/edit-subscription/${record.id}`)}
            className={`mx-1.5 cursor-pointer ${
              !checkPermissionAction(
                "Subscriptions",
                permissionJson,
                "edit"
              )
                ? "text-gray-400 cursor-not-allowed"
                : ""
            }`} />
          <DeleteOutlined onClick={() => {
            setIsDeleteModalOpen(true);
            setItemData(record);
            if (
              checkPermissionAction(
                "Subscriptions",
                permissionJson,
                "delete"
              )
            ) {
              setIsDeleteModalOpen(false);
            }
          }}/>
        </div>
      ),
    },
  ];

  return (
    <Card>
      <div className="block sm:flex justify-between mb-4 gap-5">
        <div className="flex justify-between">
          <Typography className="text-xl font-semibold" align={"left"}>
            Manage Subscriptions
          </Typography>
        </div>
        <div className="flex gap-4 items-center mb-4">
            <Input.Search
              placeholder="Search by Subscription ID or Plan Name"
              allowClear
              onChange={(e) => {
                setSearchValue(e.target.value);
                setPaginationModel({ ...paginationModel, page: 1 });
              }}
              style={{ width: 300 }}
            />
     
        <Button
          type="primary"
          size={14}
          icon={<PlusOutlined />}
          className="mx-1.5 cursor-pointer"
          onClick={() => navigate("/add-subscription")}
        >
          Add new Subscription
        </Button>
       </div>
      </div>
      <div>
        <Table
          columns={columns}
          dataSource={list}
          pagination={{
            current: paginationModel.page,
            pageSize: paginationModel.pageSize,
            total: totalRows,
            onChange: (page, pageSize) => {
              setPaginationModel({ page, pageSize });
            },
          }}
          sortDirections={["ascend", "descend"]}
          showSorterTooltip={false}
          // loading={loading}
        />
      </div>
       {/* delete modal */}
       {isDeleteModalOpen && (
        <ConfirmationModal
          ConfirmationHeading="Delete"
          ConfirmationParagraph="Are you sure you want to delete this Role?"
          isOpen={isDeleteModalOpen}
          onOk={handleDeleteOk}
          onCancel={handleDeleteCancel}
        />
      )}
    </Card>
  );
}

export default ManageSubscriptions;